import React, { useEffect, useState } from "react";
import { FolderOpen, Plus, AlertTriangle, FileText, Trash2, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, todayISO } from "@/lib/format";
import { PageHeader, Loading, Field, EmptyState } from "@/components/common";
import { DataTable, Btn, TextInput, Select } from "@/components/DataTable";
import { Modal } from "@/components/Modal";

const CATS = ["General", "Compliance", "Certification", "Report", "Contract", "Insurance", "Method Statement", "Risk Assessment"];

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const blank = { title: "", category: "General", file_name: "", file_url: "", file_id: null, version: "1.0", expiry_date: "", notes: "" };
  const [form, setForm] = useState(blank);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/documents"); setRows(data); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(blank); setModal(true); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd);
      setForm((f) => ({ ...f, file_id: data.id, file_name: data.filename, file_url: data.url, title: f.title || data.filename }));
      toast.success("File uploaded to secure storage");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const download = async (d) => {
    if (d.file_id) {
      try {
        const res = await api.get(`/files/${d.file_id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) { toast.error(apiError(e)); }
    } else if (d.file_url) { window.open(d.file_url, "_blank"); }
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    try { await api.post("/documents", form); toast.success("Document registered"); setModal(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (d) => { await api.delete(`/documents/${d.id}`); load(); };

  const expiringSoon = rows.filter((r) => r.expiry_date && new Date(r.expiry_date) < new Date(Date.now() + 60 * 864e5));

  const columns = [
    { key: "title", label: "Document", className: "font-medium text-slate-800", render: (r) => <span className="flex items-center gap-2"><FileText size={15} className="text-slate-400" /> {r.title}</span> },
    { key: "category", label: "Category", render: (r) => <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{r.category}</span> },
    { key: "version", label: "Version" },
    { key: "uploaded_by", label: "Uploaded by" },
    { key: "upload_date", label: "Date", render: (r) => fmtDate(r.upload_date) },
    { key: "expiry_date", label: "Expiry", render: (r) => r.expiry_date ? <span className={new Date(r.expiry_date) < new Date(Date.now() + 60 * 864e5) ? "text-amber-600 font-medium" : ""}>{fmtDate(r.expiry_date)}</span> : "—" },
    { key: "actions", label: "", align: "right", render: (r) => (
      <span className="flex items-center justify-end gap-1">
        {(r.file_id || (r.file_url && r.file_url.startsWith("http"))) && <button onClick={(e) => { e.stopPropagation(); download(r); }} data-testid={`doc-download-${r.id}`} className="p-1.5 text-slate-400 hover:text-seq-green"><Download size={15} /></button>}
        <button onClick={(e) => { e.stopPropagation(); del(r); }} data-testid={`doc-delete-${r.id}`} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
      </span>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Documents" subtitle="Central registry with expiry tracking for certifications and compliance" icon={FolderOpen}
        actions={<Btn onClick={openNew} data-testid="new-document-btn"><Plus size={16} /> Register Document</Btn>} />
      {expiringSoon.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-2 text-sm text-amber-700">
          <AlertTriangle size={16} /> {expiringSoon.length} document{expiringSoon.length > 1 ? "s" : ""} expiring within 60 days
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <Loading /> : <DataTable columns={columns} rows={rows} empty={<EmptyState icon={FolderOpen} title="No documents registered" hint="Upload a file to secure storage or register an external link." />} />}
      </div>
      <Modal open={modal} onOpenChange={setModal} title="Register document"
        footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-document-btn">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Title" required className="col-span-2"><TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Category"><Select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATS.map((c) => <option key={c}>{c}</option>)}</Select></Field>
          <Field label="Version"><TextInput value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></Field>
          <Field label="File name"><TextInput value={form.file_name} onChange={(e) => setForm({ ...form, file_name: e.target.value })} placeholder="audit-report.pdf" /></Field>
          <Field label="Expiry date"><TextInput type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
          <Field label="Upload file" className="col-span-2">
            <label data-testid="doc-file-input" className="flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 cursor-pointer hover:border-seq-green">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              <span className="truncate">{form.file_id ? form.file_name : (uploading ? "Uploading…" : "Choose a file to upload to secure storage")}</span>
              <input type="file" className="hidden" onChange={onFile} disabled={uploading} />
            </label>
          </Field>
          <Field label="Or external link / URL" className="col-span-2"><TextInput value={form.file_id ? "" : form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value, file_id: null, file_name: "" })} placeholder="https://…" /></Field>
        </div>
      </Modal>
    </div>
  );
}
